import { useMutation } from "@tanstack/react-query";
import Button from "components/ui/Button";
import Textarea from "components/formElements/Textarea/Textarea";
import GoogleTranslateAttributionBadge from "components/GoogleTranslateAttributionBadge/GoogleTranslateAttributionBadge";
import LanguageSelect from "components/LanguageSelect/LanguageSelect";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { useUser } from "contexts/UserContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Language } from "services/config";
import { MAX_TRANSLATION_LENGTH, translateTextMutation } from "services/translate";
import { getErrorComponents } from "services/util";
import { isRtlLang } from "utils/rtl";
import SwitchIcon from "./images/switch.svg?react";
import "./TranslationTool.css";

const DEFAULT_SRC_LANG = "en";

type SelectedLanguage = Language | undefined;

type TranslationToolProps = {
  searchText: string;
};

export default function TranslationTool(props: TranslationToolProps) {
  const { t } = useTranslation();
  const nativeLanguage = useUser().profile.native_language;

  const [srcText, setSrcText] = useState("");
  const [srcLang, setSrcLang] = useState<string | null>(DEFAULT_SRC_LANG);
  const [targetLang, setTargetLang] = useState<string | null>(nativeLanguage);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { reset, ...translation } = useMutation(translateTextMutation());
  const translatedText = translation.data?.translated_text ?? "";

  // A changed search term becomes the source text and voids the old
  // translation; an unchanged term keeps the user's in-tab edits.
  useEffect(() => {
    if (props.searchText) {
      setSrcText(props.searchText);
      reset();
    }
  }, [props.searchText, reset]);

  const translate = (text: string, source: string | null, target: string | null) => {
    const errors: string[] = [];
    if (!text) errors.push("Tool.pleaseEnterText");
    if (!target) errors.push("Tool.pleaseSelectLang");
    setValidationErrors(errors);

    if (text && target) {
      translation.mutate({ text, source_lang: source ?? undefined, target_lang: target });
    } else {
      reset();
    }
  };

  const onTargetLangChanged = (lang: SelectedLanguage) => {
    const targetLanguage = lang?.code ?? null;
    setTargetLang(targetLanguage);
    if (targetLanguage && srcText) {
      translate(srcText, srcLang, targetLanguage);
    } else {
      reset();
    }
  };

  const switchLanguages = () => {
    if (translation.isPending) return;

    setSrcText(translatedText);
    setSrcLang(targetLang);
    setTargetLang(srcLang);
    translate(translatedText, targetLang, srcLang);
  };

  return (
    <div className="translation-tool">
      {validationErrors.map((error, index) => (
        <div className="talaera-form-error" key={index}>
          {error.includes("Tool.") ? t(error) : error}
        </div>
      ))}

      {translation.isError && getErrorComponents(translation.error)}

      <div className="translation-input">
        <LanguageSelect
          value={srcLang}
          onChange={lang => setSrcLang(lang ? lang.code : null)}
        />
        <Textarea
          value={srcText}
          placeholder={t("Tool.enterWord")}
          maxLength={MAX_TRANSLATION_LENGTH}
          onChange={event => setSrcText(event.target.value)}
          rtl={isRtlLang(srcLang)}
        />
      </div>

      <div className="actions">
        <Button
          variant="secondary"
          className="switch-btn"
          title={t("Tool.switchLang")}
          aria-label={t("Tool.switchLang")}
          onClick={switchLanguages}
        >
          <SwitchIcon aria-hidden className="switch" />
        </Button>
        <Button onClick={() => translate(srcText, srcLang, targetLang)}>
          {t("Tool.Translate")}
        </Button>
      </div>

      <div className="translation-input">
        <LanguageSelect value={targetLang} onChange={onTargetLangChanged} />
        {translation.isPending ? (
          <div className="spinner-container">
            <TalaeraSpinner />
          </div>
        ) : (
          <Textarea
            value={translatedText}
            placeholder={t("Tool.findTranslation")}
            readOnly={true}
            disabled={true}
            rtl={isRtlLang(targetLang)}
          />
        )}
      </div>

      <GoogleTranslateAttributionBadge />
    </div>
  );
}
